import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, orderBy, Timestamp, where, getDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAuth } from 'firebase/auth';

export interface LedgerEntry {
  id?: string;
  clientId: string;
  clientName: string;
  type: 'payment' | 'advance';
  amount: number;
  description?: string;
  date: string;
  reference?: string;
  month: string;
  year: number;
  createdAt?: any;
  updatedAt?: any;
}

export interface ClientBalance {
  clientId: string;
  clientName: string;
  totalSales: number;
  totalPayments: number;
  totalAdvances: number;
  balance: number;
}

export interface MonthlyReport {
  month: string;
  year: number;
  totalSales: number;
  totalPayments: number;
  totalAdvances: number;
  netAmount: number;
  pendingPayments: number;
  clientBalances: ClientBalance[];
}

class LedgerService {
  private readonly COLLECTION_NAME = 'ledger';

  private async checkAuth() {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('Please log in to continue');
    }
    
    // Wait a bit for the auth state to fully propagate
    await new Promise(resolve => setTimeout(resolve, 100));
    
    if (!auth.currentUser) {
      throw new Error('Authentication failed. Please try logging in again.');
    }
    
    console.log('Auth check passed for user:', auth.currentUser.email);
  }

  private async ensureCollectionExists() {
    try {
      // Try to read from the collection to ensure it exists
      const testQuery = query(collection(db, this.COLLECTION_NAME), orderBy('createdAt', 'desc'));
      await getDocs(testQuery);
    } catch (error: any) {
      if (error.code === 'permission-denied' || error.code === 'missing-permissions') {
        console.log('Collection might not exist, attempting to create first entry...');
        // The collection will be created automatically when we add the first document
      }
    }
  }

  // Test method to verify Firebase connection and permissions
  async testConnection(): Promise<boolean> {
    try {
      await this.checkAuth();
      console.log('Authentication test passed');
      
      // Try to read from the collection
      const testQuery = query(collection(db, this.COLLECTION_NAME));
      await getDocs(testQuery);
      console.log('Collection read test passed');
      
      return true;
    } catch (error: any) {
      console.error('Connection test failed:', error);
      return false;
    }
  }

  async addLedgerEntry(entry: Omit<LedgerEntry, 'id' | 'month' | 'year' | 'createdAt' | 'updatedAt'>): Promise<LedgerEntry> {
    try {
      await this.checkAuth();
      await this.ensureCollectionExists();
      
      const date = new Date(entry.date);
      const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const year = date.getFullYear();
      const now = Timestamp.now();

      const entryData: LedgerEntry = {
        ...entry,
        month,
        year,
        date: Timestamp.fromDate(date).toDate().toISOString(),
        createdAt: now,
        updatedAt: now
      };

      console.log('Attempting to add ledger entry:', entryData);
      console.log('Current user:', getAuth().currentUser?.email);
      console.log('Collection name:', this.COLLECTION_NAME);
      
      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), entryData);
      console.log('Ledger entry added successfully with ID:', docRef.id);
      
      return { ...entryData, id: docRef.id };
    } catch (error: any) {
      console.error('Error adding ledger entry:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error details:', error);
      
      // Provide more specific error messages
      if (error.code === 'permission-denied') {
        throw new Error('Access denied. Please check your permissions or try logging in again.');
      } else if (error.code === 'unauthenticated') {
        throw new Error('You are not authenticated. Please log in again.');
      } else if (error.code === 'resource-exhausted') {
        throw new Error('Service temporarily unavailable. Please try again later.');
      } else if (error.code === 'failed-precondition') {
        throw new Error('Operation failed due to a precondition. Please try again.');
      } else if (error.code === 'aborted') {
        throw new Error('Operation was aborted. Please try again.');
      } else if (error.code === 'unavailable') {
        throw new Error('Service is currently unavailable. Please try again later.');
      } else {
        throw new Error(`Failed to add ledger entry: ${error.message || 'Unknown error'}`);
      }
    }
  }

  async getLedgerEntries(): Promise<LedgerEntry[]> {
    try {
      await this.checkAuth();
      
      const q = query(
        collection(db, this.COLLECTION_NAME),
        orderBy('date', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as LedgerEntry));
    } catch (error: any) {
      // If the collection doesn't exist yet, return empty array
      if (error.code === 'permission-denied' || error.code === 'missing-permissions') {
        console.log('Ledger collection not accessible yet, returning empty array');
        return [];
      }
      console.error('Error getting ledger entries:', error);
      throw error;
    }
  }

  async getLedgerEntriesByClient(clientId: string): Promise<LedgerEntry[]> {
    try {
      await this.checkAuth();
      
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('clientId', '==', clientId),
        orderBy('date', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as LedgerEntry));
    } catch (error: any) {
      // If the collection doesn't exist yet, return empty array
      if (error.code === 'permission-denied' || error.code === 'missing-permissions') {
        console.log('Ledger collection not accessible yet, returning empty array');
        return [];
      }
      console.error('Error getting ledger entries by client:', error);
      throw error;
    }
  }

  async getLedgerEntriesByMonth(month: string): Promise<LedgerEntry[]> {
    try {
      await this.checkAuth();
      
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('month', '==', month),
        orderBy('date', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as LedgerEntry));
    } catch (error: any) {
      // If the collection doesn't exist yet, return empty array
      if (error.code === 'missing-permissions') {
        console.log('Ledger collection not accessible yet, returning empty array');
        return [];
      }
      console.error('Error getting ledger entries by month:', error);
      throw error;
    }
  }

  async updateLedgerEntry(id: string, entry: Partial<LedgerEntry>): Promise<void> {
    try {
      await this.checkAuth();
      
      const entryRef = doc(db, this.COLLECTION_NAME, id);
      const updateData = {
        ...entry,
        updatedAt: Timestamp.now()
      };
      await updateDoc(entryRef, updateData);
    } catch (error) {
      console.error('Error updating ledger entry:', error);
      throw error;
    }
  }

  async deleteLedgerEntry(id: string): Promise<void> {
    try {
      await this.checkAuth();
      
      const entryRef = doc(db, this.COLLECTION_NAME, id);
      await deleteDoc(entryRef);
    } catch (error) {
      console.error('Error deleting ledger entry:', error);
      throw error;
    }
  }

  async getClientBalance(clientId: string): Promise<ClientBalance> {
    try {
      await this.checkAuth();
      
      const salesQuery = query(
        collection(db, 'sales'),
        where('clientId', '==', clientId)
      );
      const salesSnapshot = await getDocs(salesQuery);
      const totalSales = salesSnapshot.docs.reduce((sum, doc) => {
        const data = doc.data();
        return sum + (data.total || 0);
      }, 0);

      let totalPayments = 0;
      let totalAdvances = 0;

      try {
        const paymentsQuery = query(
          collection(db, this.COLLECTION_NAME),
          where('clientId', '==', clientId),
          where('type', '==', 'payment')
        );
        const paymentsSnapshot = await getDocs(paymentsQuery);
        totalPayments = paymentsSnapshot.docs.reduce((sum, doc) => {
          const data = doc.data();
          return sum + (data.amount || 0);
        }, 0);
      } catch (error: any) {
        if (error.code === 'permission-denied' || error.code === 'missing-permissions') {
          console.log('Ledger collection not accessible yet, using 0 for payments');
        } else {
          throw error;
        }
      }

      try {
        const advancesQuery = query(
          collection(db, this.COLLECTION_NAME),
          where('clientId', '==', clientId),
          where('type', '==', 'advance')
        );
        const advancesSnapshot = await getDocs(advancesQuery);
        totalAdvances = advancesSnapshot.docs.reduce((sum, doc) => {
          const data = doc.data();
          return sum + (data.amount || 0);
        }, 0);
      } catch (error: any) {
        if (error.code === 'permission-denied' || error.code === 'missing-permissions') {
          console.log('Ledger collection not accessible yet, using 0 for advances');
        } else {
          throw error;
        }
      }

      // Calculate client balance:
      // Balance = Total Sales - (Total Payments + Total Advances)
      // Positive balance means client owes money
      // Negative balance means we owe the client (overpayment or advance)
      const balance = totalSales - (totalPayments + totalAdvances);

      const clientDoc = await getDoc(doc(db, 'clients', clientId));
      const clientName = clientDoc.exists() ? clientDoc.data().name : 'Unknown Client';

      return {
        clientId,
        clientName,
        totalSales,
        totalPayments,
        totalAdvances,
        balance
      };
    } catch (error) {
      console.error('Error getting client balance:', error);
      throw error;
    }
  }

  async getAllClientBalances(): Promise<ClientBalance[]> {
    try {
      await this.checkAuth();
      
      const clientsQuery = query(collection(db, 'clients'));
      const clientsSnapshot = await getDocs(clientsQuery);
      
      const balances: ClientBalance[] = [];
      
      for (const clientDoc of clientsSnapshot.docs) {
        const balance = await this.getClientBalance(clientDoc.id);
        balances.push(balance);
      }
      
      return balances.sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance));
    } catch (error) {
      console.error('Error getting all client balances:', error);
      throw error;
    }
  }

  async generateMonthlyReport(month: string, year: number): Promise<MonthlyReport> {
    try {
      await this.checkAuth();
      
      const startDate = new Date(year, parseInt(month.split('-')[1]) - 1, 1);
      const endDate = new Date(year, parseInt(month.split('-')[1]), 0);
      
      const salesQuery = query(
        collection(db, 'sales'),
        where('date', '>=', startDate.toISOString()),
        where('date', '<=', endDate.toISOString())
      );
      const salesSnapshot = await getDocs(salesQuery);
      const totalSales = salesSnapshot.docs.reduce((sum, doc) => {
        const data = doc.data();
        return sum + (data.total || 0);
      }, 0);

      let totalPayments = 0;
      let totalAdvances = 0;

      try {
        const ledgerQuery = query(
          collection(db, this.COLLECTION_NAME),
          where('month', '==', month)
        );
        const ledgerSnapshot = await getDocs(ledgerQuery);
        
        totalPayments = ledgerSnapshot.docs
          .filter(doc => doc.data().type === 'payment')
          .reduce((sum, doc) => sum + doc.data().amount, 0);
        
        totalAdvances = ledgerSnapshot.docs
          .filter(doc => doc.data().type === 'advance')
          .reduce((sum, doc) => sum + doc.data().amount, 0);
      } catch (error: any) {
        if (error.code === 'permission-denied' || error.code === 'missing-permissions') {
          console.log('Ledger collection not accessible yet, using 0 for payments and advances');
        } else {
          throw error;
        }
      }

      // Calculate net amount for the month:
      // Net Amount = Total Sales - (Total Payments + Total Advances)
      // Positive net amount means money is owed to the business
      // Negative net amount means the business owes money (overpayment or advance)
      const netAmount = totalSales - (totalPayments + totalAdvances);
      const pendingPayments = Math.max(0, netAmount);

      const clientBalances = await this.getAllClientBalances();

      return {
        month,
        year,
        totalSales,
        totalPayments,
        totalAdvances,
        netAmount,
        pendingPayments,
        clientBalances
      };
    } catch (error) {
      console.error('Error generating monthly report:', error);
      throw error;
    }
  }

  async getPendingPayments(): Promise<ClientBalance[]> {
    try {
      const balances = await this.getAllClientBalances();
      return balances.filter(balance => balance.balance > 0);
    } catch (error: any) {
      if (error.code === 'permission-denied' || error.code === 'missing-permissions') {
        console.log('Ledger collection not accessible yet, returning empty array');
        return [];
      }
      console.error('Error getting pending payments:', error);
      throw error;
    }
  }

  async getOutstandingAdvances(): Promise<ClientBalance[]> {
    try {
      const balances = await this.getAllClientBalances();
      return balances.filter(balance => balance.balance < 0);
    } catch (error: any) {
      if (error.code === 'permission-denied' || error.code === 'missing-permissions') {
        console.log('Ledger collection not accessible yet, returning empty array');
        return [];
      }
      console.error('Error getting outstanding advances:', error);
      throw error;
    }
  }
}

export const ledgerService = new LedgerService(); 